import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { IvrInstanceDto } from '../../models/ivr-instance.dto';
import { IvrInstanceFormComponent } from '../ivr-instance-form/ivr-instance-form.component';
import { IvrInstanceListComponent } from '../ivr-instance-list/ivr-instance-list.component';
import { IvrInstanceToolbarComponent } from '../ivr-instance-toolbar/ivr-instance-toolbar.component';

@Component({
  selector: 'app-ivr-instance-main',
  standalone: true,
  imports: [
    CommonModule,
    IvrInstanceToolbarComponent,
    IvrInstanceListComponent,
    IvrInstanceFormComponent,
  ],
  templateUrl: './ivr-instance-main.component.html',
  styleUrls: ['./ivr-instance-main.component.scss'],
})
export class IvrInstanceMainComponent implements OnInit {
  private readonly crudService = inject(CrudService);
  private readonly toaster = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);

  instances: IvrInstanceDto[] = [];
  loading = true;
  showFormModal = false;
  selectedInstance: IvrInstanceDto | null = null;
  isEditMode = false;

  ngOnInit(): void {
    this.loadList();
  }

  loadList(): void {
    this.loading = true;

    this.crudService.get<IvrInstanceDto>(IvrInstanceDto, null).subscribe({
      next: (response: ListResponseDto<IvrInstanceDto>) => {
        this.instances = response.items ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openAddModal(): void {
    this.selectedInstance = null;
    this.isEditMode = false;
    this.showFormModal = true;
  }

  openEditModal(instance: IvrInstanceDto): void {
    this.selectedInstance = instance;
    this.isEditMode = true;
    this.showFormModal = true;
  }

  onFormVisibilityChange(visible: boolean): void {
    this.showFormModal = visible;

    if (!visible) {
      this.selectedInstance = null;
    }
  }

  onFormSaved(isCreate: boolean): void {
    this.showFormModal = false;
    this.selectedInstance = null;
    this.toaster.success(
      isCreate
        ? 'FreeswitchIntegration::IvrInstanceSuccessfullyCreated'
        : 'FreeswitchIntegration::IvrInstanceSuccessfullyUpdated',
      '',
      null,
    );
    this.loadList();
  }

  openDeleteModal(instance: IvrInstanceDto): void {
    this.confirmationService
      .warn('FreeswitchIntegration::AreYouSureDeleteIvr', 'FreeswitchIntegration::ConfirmDelete', {
        hideCancelBtn: false,
        hideYesBtn: false,
        dismissible: true,
        yesText: 'FreeswitchIntegration::Delete',
        cancelText: 'FreeswitchIntegration::Cancel',
      })
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService.delete(IvrInstanceDto, instance.id).pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: done => {
          if (done) {
            this.toaster.success('FreeswitchIntegration::IvrInstanceSuccessfullyDeleted', '', null);
            this.loadList();
          }
        },
      });
  }
}
